import { formatString, isSameOrAfter } from '@common/utils';
import { isSameDay } from 'date-fns';
import { FormikConfig, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { createTimeSpentFromProject, updateTimeSpentFromProject } from '~/@user-store/timeSpent/timeSpent.requests';
import {
    Project,
    ProjectTimeSpentRecord,
    TimeSpentDumpLoad,
    TimeSpentUser,
} from '~/@views/UserView/ProjectPage/ProjectTimeSpent/types';
import i18n from '~/i18n';
import { showConfirmDialog } from '~/services/dialog';

const MAX_HOURS_PER_DAY = 8;

export type IFormValues = {
    project: Project;
    dumpLoad: TimeSpentDumpLoad | null;
    user: TimeSpentUser | null;
    date: Date | null;
    endDate: Date | null;
    hoursSpent: number | null;
    licensePlate: string;
    comment: string;
};

export const getProjectTimeSpentFormik = (
    project: Project,
    timeSpent: ProjectTimeSpentRecord | null,
    onClose: () => void
): FormikConfig<IFormValues> => ({
    initialValues: {
        project,
        dumpLoad: timeSpent?.dumpLoadNumber
            ? project.dumpLoads.find(d => d.serialNumber === timeSpent.dumpLoadNumber) || null
            : null,
        user: timeSpent?.user || null,
        date: timeSpent?.date ? new Date(timeSpent.date) : null,
        endDate: timeSpent?.endDate ? new Date(timeSpent.endDate) : null,
        hoursSpent: timeSpent?.hoursSpent || 0,
        licensePlate: timeSpent?.licensePlate || '',
        comment: timeSpent?.comment || '',
    },
    validationSchema: Yup.object().shape({
        dumpLoad: Yup.object()
            .nullable()
            .required(formatString(i18n.isRequired, i18n.ProjectDocuments.timeSpent.dumpLoadNumber)),
        user: Yup.object().nullable().required(formatString(i18n.isRequired, i18n.ProjectDocuments.timeSpent.user)),
        date: Yup.date().nullable().required(formatString(i18n.isRequired, i18n.ProjectDocuments.timeSpent.date)),
        endDate: Yup.date()
            .nullable()
            .test('endDate validation', '', function (this, value) {
                if (value && this.parent.date && !isSameOrAfter(value, this.parent.date))
                    return this.createError({
                        message: i18n.errorCodes.END_DATE_IS_BEFORE_START_DATE,
                        path: 'endDate',
                    });

                return true;
            }),
        hoursSpent: Yup.number().min(0.001, i18n.ProjectDocuments.timeSpent.hoursSpentIncorrect),
        licensePlate: Yup.string()
            .nullable()
            .trim()
            .required(formatString(i18n.isRequired, i18n.ProjectDocuments.timeSpent.licensePlate)),
    }),
    onSubmit: async values => {
        const { dumpLoad, user, date, endDate, hoursSpent, licensePlate, comment } = values;

        if (!dumpLoad || !date || !hoursSpent || !user) return;

        if (hoursSpent > MAX_HOURS_PER_DAY && (!endDate || isSameDay(date, endDate))) {
            const answer = await showConfirmDialog({
                title: i18n.ProjectDocuments.timeSpent.saveInfo,
                message: formatString(i18n.ProjectDocuments.timeSpent.saveConfirmation, MAX_HOURS_PER_DAY.toString()),
                okLabel: i18n.continue,
                cancelLabel: i18n.back,
            });

            if (!answer) return;
        }

        if (timeSpent) {
            await updateTimeSpentFromProject(project.id, timeSpent.id, {
                date: date.toISOString(),
                endDate: endDate?.toISOString(),
                hoursSpent,
                licensePlate,
                comment,
            });
        } else {
            await createTimeSpentFromProject(project.id, {
                dumpLoadNumber: dumpLoad.serialNumber,
                date: date.toISOString(),
                endDate: endDate?.toISOString(),
                hoursSpent,
                licensePlate,
                comment,
                userId: user.id,
            });
        }

        onClose();
    },
});

export const useProjectTimeSpentFormikContext = () => useFormikContext<IFormValues>();
